import { queryClient } from 'app/queryClient';
import { resetClientAuthState } from 'api/resetClientAuthState';
import { setRole, setUserRoleIds } from 'api/tokenStorage';
import { applyAuthSession, AUTH_SESSION_QUERY_KEY } from 'hooks/useAuthSession';
import type { OtpVerifyResponse } from 'hooks/useOtpChallenge';
import { clearVerificationBrowserKey, readPodborDraft } from 'utils/podborDraft';
import { resolvePostLoginNavigation } from 'utils/postLoginNavigation';
import { consumeReturnTo, resolveRegistrationNavigation } from 'utils/returnTo';

type CabinetEntry = {
  id?: number;
  email?: string | null;
  phone?: string | null;
  roles?: number[] | null;
  session_started?: boolean;
  phone_verified?: boolean;
  email_verified?: boolean;
  has_password?: boolean;
  phone_verified_at?: string | null;
  email_verified_at?: string | null;
};

function rememberSession(data: CabinetEntry, roles: number[], preferredRoleId: number | null) {
  void queryClient.cancelQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
  resetClientAuthState(queryClient);
  setRole(roles, preferredRoleId);
  setUserRoleIds(roles);
  applyAuthSession({ ...data, id: data.id as number, roles });
}

export function completeOtpAuthSession(
  data: OtpVerifyResponse,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  returnTo?: string | null,
): boolean {
  if (!data.session_started || !data.id) return false;
  const roles = Array.isArray(data.roles) ? data.roles.map((role) => Number(role)) : [];
  const { path, preferredRoleId } = resolvePostLoginNavigation(roles, returnTo);
  if (path === '/login') return false;
  rememberSession(data, roles, preferredRoleId);
  navigate(path, { replace: true });
  return true;
}

/** Open the cabinet after registration. Does not send the person to the login page. */
export function enterCabinetAfterRegistration(
  data: CabinetEntry,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
): boolean {
  if (!data.id || data.session_started === false) return false;
  const registrationRole = localStorage.getItem('registration_role');
  const studentType =
    localStorage.getItem('registration_student_type') || readPodborDraft()?.registrantType;
  const landing = resolveRegistrationNavigation({
    roles: data.roles,
    registrationRole,
    studentType,
    returnTo: consumeReturnTo(),
  });
  if (!landing) return false;
  rememberSession(data, landing.roles, landing.preferredRoleId);
  localStorage.removeItem('registration_email');
  localStorage.removeItem('registration_role');
  localStorage.removeItem('registration_student_type');
  clearVerificationBrowserKey();
  navigate(landing.path, { replace: true });
  return true;
}
